import React, { useState, useEffect } from 'react';
import { getApiBaseUrl } from '../../services/api';

function SystemStatusPage() {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchStatus();
        // 30초마다 갱신
        const interval = setInterval(fetchStatus, 30000);
        return () => clearInterval(interval);
    }, []);

    const fetchStatus = async () => {
        try {
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            const response = await fetch(`${getApiBaseUrl()}/platform/status`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            const result = await response.json();
            if (result.success) {
                setStatus(result.data);
            }
        } catch (error) {
            console.error('시스템 상태 조회 실패:', error);
        } finally {
            setLoading(false);
        }
    };

    // 기본 데이터 (API에서 제공하지 않는 정보용)
    const defaultStatus = {
        services: [
            { name: 'Spring Boot API', status: 'running', url: 'https://smi-api.seoro.ai.kr', uptime: '99.9%' },
            { name: 'AI Service', status: 'running', url: 'https://smi-ai.seoro.ai.kr', uptime: '99.5%' },
            { name: 'PostgreSQL', status: 'running', url: 'localhost:5432', uptime: '100%' },
            { name: 'MariaDB (Raw)', status: 'running', url: '192.168.219.200:3306', uptime: '99.8%' },
            { name: 'LM Studio', status: 'running', url: '192.168.219.200:1234', uptime: '98.5%' }
        ],
        etl: {
            lastRun: '2024-12-12 14:30:00',
            status: 'SUCCESS',
            recordsProcessed: 1250,
            syncInterval: '30초'
        },
        database: {
            totalTables: 45,
            totalRecords: '2.5M',
            dbSize: '1.2 GB'
        }
    };

    // API 응답과 기본 데이터 병합 (API에 없는 필드는 기본값 사용)
    const displayStatus = {
        services: status?.services || defaultStatus.services,
        etl: status?.etl || defaultStatus.etl,
        database: status?.database || defaultStatus.database,
        companies: status?.companies || { total: 0, active: 0 },
        users: status?.users || { total: 0, active: 0 }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'running': return '#22C55E';
            case 'warning': return '#F59E0B';
            case 'error': return '#EF4444';
            default: return '#9CA3AF';
        }
    };

    return (
        <div className="platform-page">
            <div className="platform-page-header">
                <h1 className="platform-page-title">시스템 현황</h1>
                <p className="platform-page-description">플랫폼 전체 서비스 상태를 모니터링합니다.</p>
            </div>

            {/* 전체 통계 */}
            <div className="platform-stats-grid">
                <div className="platform-stat-card">
                    <div className="platform-stat-icon">
                        <span className="material-symbols-outlined">business</span>
                    </div>
                    <div className="platform-stat-info">
                        <div className="platform-stat-value">{displayStatus.companies.total}</div>
                        <div className="platform-stat-label">전체 회사</div>
                    </div>
                </div>
                <div className="platform-stat-card">
                    <div className="platform-stat-icon">
                        <span className="material-symbols-outlined">check_circle</span>
                    </div>
                    <div className="platform-stat-info">
                        <div className="platform-stat-value">{displayStatus.companies.active}</div>
                        <div className="platform-stat-label">활성 회사</div>
                    </div>
                </div>
                <div className="platform-stat-card">
                    <div className="platform-stat-icon">
                        <span className="material-symbols-outlined">group</span>
                    </div>
                    <div className="platform-stat-info">
                        <div className="platform-stat-value">{displayStatus.users.total}</div>
                        <div className="platform-stat-label">전체 사용자</div>
                    </div>
                </div>
                <div className="platform-stat-card">
                    <div className="platform-stat-icon">
                        <span className="material-symbols-outlined">person_check</span>
                    </div>
                    <div className="platform-stat-info">
                        <div className="platform-stat-value">{displayStatus.users.active}</div>
                        <div className="platform-stat-label">활성 사용자</div>
                    </div>
                </div>
            </div>

            {/* 서비스 상태 */}
            <div className="platform-table-container" style={{ marginBottom: '24px' }}>
                <div className="platform-table-header">
                    <h3 className="platform-table-title">서비스 상태</h3>
                    <button className="platform-btn platform-btn-secondary" onClick={fetchStatus}>
                        <span className="material-symbols-outlined">refresh</span>
                        새로고침
                    </button>
                </div>
                <table className="platform-table">
                    <thead>
                        <tr>
                            <th>서비스</th>
                            <th>상태</th>
                            <th>URL</th>
                            <th>가동률</th>
                        </tr>
                    </thead>
                    <tbody>
                        {displayStatus.services.map((service, index) => (
                            <tr key={index}>
                                <td><strong>{service.name}</strong></td>
                                <td>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span style={{
                                            width: '8px',
                                            height: '8px',
                                            borderRadius: '50%',
                                            background: getStatusColor(service.status)
                                        }} />
                                        {service.status === 'running' ? '실행 중' : service.status}
                                    </div>
                                </td>
                                <td><code style={{ color: '#9CA3AF' }}>{service.url}</code></td>
                                <td>{service.uptime}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* ETL 상태 & DB 상태 */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                {/* ETL 현황 */}
                <div className="platform-table-container">
                    <div className="platform-table-header">
                        <h3 className="platform-table-title">ETL 현황</h3>
                    </div>
                    <div style={{ padding: '20px' }}>
                        <div style={{ display: 'grid', gap: '16px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ color: '#9CA3AF' }}>마지막 실행</span>
                                <span style={{ color: '#E5E7EB' }}>{displayStatus.etl.lastRun || '-'}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ color: '#9CA3AF' }}>상태</span>
                                <span className={`platform-badge ${displayStatus.etl.status === 'SUCCESS' ? 'active' : 'inactive'}`}>
                                    {displayStatus.etl.status === 'SUCCESS' ? '성공' :
                                     displayStatus.etl.status === 'NO_DATA' ? '데이터 없음' :
                                     displayStatus.etl.status === 'ERROR' ? '오류' : displayStatus.etl.status}
                                </span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ color: '#9CA3AF' }}>처리 레코드</span>
                                <span style={{ color: '#E5E7EB' }}>{(displayStatus.etl.recordsProcessed || 0).toLocaleString()}건</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ color: '#9CA3AF' }}>동기화 주기</span>
                                <span style={{ color: '#E5E7EB' }}>{displayStatus.etl.syncInterval}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 데이터베이스 현황 */}
                <div className="platform-table-container">
                    <div className="platform-table-header">
                        <h3 className="platform-table-title">데이터베이스 현황</h3>
                    </div>
                    <div style={{ padding: '20px' }}>
                        <div style={{ display: 'grid', gap: '16px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ color: '#9CA3AF' }}>총 테이블</span>
                                <span style={{ color: '#E5E7EB' }}>{displayStatus.database.totalTables}개</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ color: '#9CA3AF' }}>총 레코드</span>
                                <span style={{ color: '#E5E7EB' }}>{displayStatus.database.totalRecords}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ color: '#9CA3AF' }}>DB 사이즈</span>
                                <span style={{ color: '#E5E7EB' }}>{displayStatus.database.dbSize}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default SystemStatusPage;
